import type { ReferralRow, ReferralSummary } from '@contract';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { createColumnHelper } from '@tanstack/react-table';
import { useEffect, useState } from 'react';
import { z } from 'zod';
import { DataTable } from '@/components/data-table';
import { ExportButton } from '@/components/export-button';
import { PageHeader } from '@/components/page-header';
import { OpenRecord } from '@/components/queue/queue-parts';
import { ReferralGrove } from '@/components/referral-grove';
import { fileReferralRows } from '@/components/referrals/referral-rows';
import { ReferralStatus } from '@/components/referrals/referral-status';
import { useOpenRecord } from '@/components/sheet/use-sheet';
import { TabLinks } from '@/components/tab-links';
import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { LinkButton } from '@/components/ui/link-button';
import { Select } from '@/components/ui/select';
import { adminQuery, useKeysetList } from '@/lib/query';
import { treeSummary } from '@/lib/referral-tree';
import { atLimit, limitUse } from '@/lib/referrals';

const searchSchema = z.object({
  view: z.enum(['list', 'tree']).optional(),
  tree_customer: z.uuid().optional(),
  status: z.enum(['pending', 'qualified', 'void']).optional(),
  void_reason: z.enum(['expired', 'shared_device', 'staff']).optional(),
  referrer: z.uuid().optional(),
  q: z.string().max(120).optional(),
});

type Search = z.infer<typeof searchSchema>;

/** Filters live in the URL, so a filtered view can be shared (decision 0007). */
export const Route = createFileRoute('/_app/referrals')({
  validateSearch: searchSchema,
  component: ReferralsPage,
});

const PAGE_SIZE = 50;

function ReferralsPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const view = search.view ?? 'list';
  return (
    <>
      <PageHeader
        title="Referrals"
        description="Who brought whom. Names and phone numbers are masked, and every look is logged. What counts as successful, and the limits, are set under Settings."
        actions={
          view === 'list' ? (
            <ExportButton source="referrals.list" args={listFilters(search)} />
          ) : null
        }
      />
      <TabLinks
        tabs={[
          { key: 'list', label: 'List' },
          { key: 'tree', label: 'Tree' },
        ]}
        active={view}
        render={(tab, domProps) => (
          <Link
            {...domProps}
            to="/referrals"
            search={{ view: tab.key as 'list' | 'tree' }}
          />
        )}
      />
      {view === 'tree' ? (
        <ReferralTree
          customerId={search.tree_customer}
          onClear={() => void navigate({ search: { view: 'tree' } })}
        />
      ) : (
        <ReferralList search={search} />
      )}
    </>
  );
}

function listFilters(search: Search) {
  return {
    status: search.status,
    void_reason: search.void_reason,
    referrer_id: search.referrer,
    q: search.q,
  };
}

function ReferralList({ search }: Readonly<{ search: Search }>) {
  const navigate = useNavigate({ from: Route.fullPath });
  const openRecord = useOpenRecord();
  const list = useKeysetList(
    'referrals.list',
    { ...listFilters(search), limit: PAGE_SIZE },
    { rows: (data) => data.rows },
  );
  const listRows = list.rows;
  useEffect(() => {
    if (listRows) fileReferralRows(listRows);
  }, [listRows]);
  const go = (next: Partial<Search>) =>
    void navigate({ search: { ...search, ...next } });

  return (
    <>
      {list.data ? (
        <Totals
          summary={list.data.summary}
          onPick={(status, void_reason) => go({ status, void_reason })}
        />
      ) : null}
      {search.referrer ? (
        <ReferrerPanel
          referrerId={search.referrer}
          onClear={() => go({ referrer: undefined })}
        />
      ) : null}
      <Filters search={search} onChange={go} />
      {list.error ? (
        <Alert variant="destructive">{list.error.message}</Alert>
      ) : null}
      <DataTable
        columns={columns}
        data={list.rows}
        more={list.more}
        empty="No referrals match these filters."
        rowLabel={(row) =>
          `Open the referral of ${row.referred_name ?? 'the referred customer'}`
        }
        onRowClick={(row, rowEl) =>
          openRecord({ type: 'referral', id: row.id }, rowEl)
        }
      />
    </>
  );
}

/** Who brought whom, several levels deep, as a grove with a ranking beside it. */
function ReferralTree({
  customerId,
  onClear,
}: Readonly<{ customerId: string | undefined; onClear: () => void }>) {
  const tree = useQuery(
    adminQuery('referrals.tree', { customer_id: customerId }),
  );
  if (tree.isError) {
    return <Alert variant="destructive">{tree.error.message}</Alert>;
  }
  if (!tree.data) {
    return <p className="text-sm text-muted-foreground">Loading the tree…</p>;
  }
  return (
    <div className="flex min-w-0 flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">
          {treeSummary(tree.data.nodes)}
        </p>
        {customerId ? (
          <Button variant="ghost" size="sm" onClick={onClear}>
            Show everyone
          </Button>
        ) : null}
      </div>
      <ReferralGrove
        key={customerId ?? 'all'}
        nodes={tree.data.nodes}
        roots={tree.data.roots}
        initialFocusId={customerId}
      />
    </div>
  );
}

function Totals({
  summary,
  onPick,
}: Readonly<{
  summary: ReferralSummary;
  onPick: (
    status: Search['status'],
    voidReason?: Search['void_reason'],
  ) => void;
}>) {
  const link = (
    label: string,
    status: Search['status'],
    reason?: Search['void_reason'],
  ) => (
    <button
      type="button"
      className="font-medium text-foreground tabular-nums hover:underline"
      onClick={() => onPick(status, reason)}
    >
      {label}
    </button>
  );
  const voids = [
    { n: summary.void_expired, text: 'ran out of time', reason: 'expired' },
    {
      n: summary.void_shared_device,
      text: 'same phone',
      reason: 'shared_device',
    },
    { n: summary.void_staff, text: 'by staff', reason: 'staff' },
  ] as const;
  return (
    <p className="text-sm text-muted-foreground">
      {link(`${summary.pending} waiting`, 'pending')} ·{' '}
      {link(`${summary.qualified} successful`, 'qualified')} (
      {summary.qualified_last_30_days} in the last 30 days)
      {summary.void > 0 ? (
        <>
          {' '}
          · {link(`${summary.void} void`, 'void')} (
          {voids
            .filter((item) => item.n > 0)
            .map((item, index) => (
              <span key={item.reason}>
                {index > 0 ? ', ' : ''}
                {link(`${item.n} ${item.text}`, 'void', item.reason)}
              </span>
            ))}
          )
        </>
      ) : null}
    </p>
  );
}

function Filters({
  search,
  onChange,
}: Readonly<{ search: Search; onChange: (next: Partial<Search>) => void }>) {
  const [text, setText] = useState(search.q ?? '');
  return (
    <div className="grid grid-cols-2 items-end gap-3 sm:flex sm:flex-wrap">
      <form
        className="col-span-2 flex w-full items-end gap-2 sm:w-96"
        onSubmit={(event) => {
          event.preventDefault();
          onChange({ q: text.trim() || undefined });
        }}
      >
        <Input
          aria-label="Search referrals"
          placeholder="Phone number, referral code or customer id"
          value={text}
          onChange={(event) => setText(event.target.value)}
        />
        <Button type="submit" variant="outline">
          Search
        </Button>
      </form>
      <Select
        label="Status"
        className="w-full sm:w-44"
        value={search.status ?? ''}
        onChange={(event) =>
          onChange({
            status: (event.target.value || undefined) as Search['status'],
            void_reason:
              event.target.value === 'void' ? search.void_reason : undefined,
          })
        }
      >
        <option value="">Any</option>
        <option value="pending">Waiting</option>
        <option value="qualified">Successful</option>
        <option value="void">Void</option>
      </Select>
      <Select
        label="Void reason"
        className="w-full sm:w-48"
        value={search.void_reason ?? ''}
        onChange={(event) =>
          onChange({
            void_reason: (event.target.value ||
              undefined) as Search['void_reason'],
            status: event.target.value ? 'void' : search.status,
          })
        }
      >
        <option value="">Any</option>
        <option value="expired">Ran out of time</option>
        <option value="shared_device">Same phone</option>
        <option value="staff">Voided by staff</option>
      </Select>
    </div>
  );
}

/** One person's code, counts and how much of the limit they have used. */
function ReferrerPanel({
  referrerId,
  onClear,
}: Readonly<{ referrerId: string; onClear: () => void }>) {
  const referrer = useQuery(
    adminQuery('referrals.referrer', { referrer_id: referrerId }),
  );
  if (referrer.isError) {
    return <Alert variant="destructive">{referrer.error.message}</Alert>;
  }
  const data = referrer.data;
  if (!data) return null;
  return (
    <Card className="gap-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="font-medium">
            Referrals by {data.name ?? 'a customer with no name yet'}
          </div>
          <div className="text-xs text-muted-foreground">
            {data.phone} · code {data.code ?? 'not made yet'}
          </div>
        </div>
        <div className="flex gap-2">
          <LinkButton
            to="/customers/$customerId"
            params={{ customerId: data.referrer_id }}
          >
            Open customer
          </LinkButton>
          <Button variant="ghost" onClick={onClear}>
            Show everyone
          </Button>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
        <span>{data.pending} waiting</span>
        <span>{limitUse(data)}</span>
        <span>{data.void} void</span>
        {data.max_pending_per_user === null ? null : (
          <span>Waiting limit {data.max_pending_per_user}</span>
        )}
        {atLimit(data) ? <Badge variant="warning">At the limit</Badge> : null}
      </div>
    </Card>
  );
}

function Person({
  id,
  name,
  phone,
}: Readonly<{ id: string; name: string | null; phone: string | null }>) {
  return (
    <OpenRecord
      sheetRef={{ type: 'cust', id }}
      label={`Open customer ${name ?? phone ?? ''}`}
      className="flex flex-col hover:underline"
    >
      <span>{name ?? 'No name yet'}</span>
      {phone ? (
        <span className="font-mono text-xs text-muted-foreground">{phone}</span>
      ) : null}
    </OpenRecord>
  );
}

/** "13 Oct 07:26" (UTC), compact for a list. */
function compactDate(value: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  const month = date.toLocaleString('en-GB', {
    month: 'short',
    timeZone: 'UTC',
  });
  return `${date.getUTCDate()} ${month} ${date.toISOString().slice(11, 16)}`;
}

const column = createColumnHelper<ReferralRow>();
const columns = [
  column.display({
    id: 'referrer',
    meta: { sortValue: (row) => row.referrer_name ?? row.referrer_phone },
    header: 'Referrer',
    cell: (info) => {
      const row = info.row.original;
      return (
        <Person
          id={row.referrer_id}
          name={row.referrer_name}
          phone={row.referrer_phone}
        />
      );
    },
  }),
  column.display({
    id: 'referred',
    meta: { sortValue: (row) => row.referred_name ?? row.referred_phone },
    header: 'Referred',
    cell: (info) => {
      const row = info.row.original;
      return (
        <Person
          id={row.referred_id}
          name={row.referred_name}
          phone={row.referred_phone}
        />
      );
    },
  }),
  column.display({
    id: 'status',
    meta: { sortValue: (row) => row.status },
    header: 'Status',
    cell: (info) => (
      <ReferralStatus
        status={info.row.original.status}
        voidReason={info.row.original.void_reason}
      />
    ),
  }),
  column.display({
    id: 'joined',
    meta: { sortValue: (row) => row.created_at },
    header: 'Joined',
    cell: (info) => (
      <span className="whitespace-nowrap text-xs">
        {compactDate(info.row.original.created_at)}
      </span>
    ),
  }),
  column.display({
    id: 'qualified',
    meta: { sortValue: (row) => row.qualified_at },
    header: 'Successful',
    cell: (info) => (
      <span className="whitespace-nowrap text-xs">
        {compactDate(info.row.original.qualified_at)}
      </span>
    ),
  }),
];
